import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Row, Col, Card, Tag, Space, Spin, Radio, message } from 'antd';
import { HeartOutlined, ShareAltOutlined } from '@ant-design/icons';
import { getProducts, getCategories } from '../../services/productService';
import SearchInput from '../SearchInput';
import ProductCard from '../ProductCard';

const FilterSection = styled.div`
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 24px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
    margin-bottom: 16px;
    gap: 12px;
  }
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  .ant-radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .ant-radio-button-wrapper {
    border-radius: 16px;
    padding: 0 16px;
    height: 32px;
    line-height: 30px;
    border-left: 1px solid #d9d9d9 !important;
    
    &:first-child {
      border-left: 1px solid #d9d9d9 !important;
    }

    &:not(:first-child)::before {
      display: none;
    }

    &.ant-radio-button-wrapper-checked {
      border-color: #D4303B !important;
      color: #D4303B;
      
      &::before {
        background-color: #D4303B !important;
      }
    }

    &:hover {
      color: #D4303B;
    }
  }
`;

const SearchBar = styled.div`
  flex-shrink: 0;
`;

const PriceTag = styled.div`
  color: #D4303B;
  font-size: 18px;
  font-weight: bold;
  margin-top: 8px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-top: 4px;
  }
`;

const TagContainer = styled(Space)`
  @media (max-width: 768px) {
    .ant-tag {
      padding: 0 4px;
      font-size: 10px;
      line-height: 18px;
      margin-right: 4px;
    }
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 20px;
  
  &.initial-loading {
    padding: 40px;
  }
`;

const LoadingMore = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #999;
  font-size: 14px;
`;

const SortBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 16px;
  padding-left: 16px;
  border-left: 1px solid #f0f0f0;

  .ant-radio-button-wrapper {
    font-size: 13px;
    padding: 0 12px;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    padding-left: 0;
    border-left: none;
    margin-top: 8px;
  }
`;

const ProductList = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [sortOrder, setSortOrder] = useState('default');
  
  // 添加分页相关状态
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 12; // 每页显示的商品数量
  const observer = useRef();
  const lastProductRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreProducts();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore]);

  const fetchCategories = async () => {
    try {
      const categoriesData = await getCategories();
      setCategories([
        { id: 'all', name: t('productList.categories.all'), description: t('productList.categories.allDescription') },
        ...categoriesData
      ]);
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('加载分类失败');
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await fetchCategories();
      await fetchProducts(1);
    } catch (error) {
      console.error('ProductList - Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (pageNum, categoryId = activeCategory) => {
    try {
      const productsData = await getProducts({
        page: pageNum,
        pageSize,
        category: categoryId === 'all' ? undefined : categoryId
      });
      
      console.log('Received products data:', productsData);
      
      // 使用返回的 products 数组
      const items = productsData.products || [];
      
      if (pageNum === 1) {
        setProducts(items);
        setFilteredProducts(items);
      } else {
        setProducts(prev => [...prev, ...items]);
        setFilteredProducts(prev => [...prev, ...items]);
      }
      
      // 使用返回的总数来判断是否还有更多数据
      const totalCount = productsData.totalProducts || 0;
      const currentCount = (pageNum - 1) * pageSize + items.length;
      setHasMore(currentCount < totalCount);
      
      console.log('Current display status:', {
        totalCount,
        currentCount,
        hasMore: currentCount < totalCount,
        items
      });
    } catch (error) {
      console.error('ProductList - Error fetching products:', error);
    }
  };

  const loadMoreProducts = async () => {
    if (!hasMore || loadingMore) return;
    
    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      await fetchProducts(nextPage);
      setPage(nextPage);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // 添加语言变化的监听
  useEffect(() => {
    // 当语言改变时，重新渲染商品列表
    setFilteredProducts(products.map(product => ({...product})));
  }, [currentLang, products]);

  // 添加分类数据的重新加载逻辑
  useEffect(() => {
    const reloadCategories = async () => {
      await fetchCategories();
    };
    
    // 监听 storage 事件，当分类数据更新时重新加载
    const handleStorageChange = (e) => {
      if (e.key === 'categoryListUpdated') {
        reloadCategories();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleCategoryChange = async (categoryId) => {
    try {
      setLoading(true);
      setActiveCategory(categoryId);
      setPage(1);
      setHasMore(true);
      setSearchValue('');
      setSortOrder('default');
      await fetchProducts(1, categoryId);
    } catch (error) {
      console.error('ProductList - Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    if (!value.trim()) {
      const sortedProducts = sortProducts(products, sortOrder);
      setFilteredProducts(sortedProducts);
      return;
    }

    const searchResult = products.filter(product => {
      const title = currentLang !== 'zh' && product.translations?.title
        ? product.translations.title.toLowerCase()
        : product.title.toLowerCase();
      
      const description = currentLang !== 'zh' && product.translations?.description
        ? product.translations.description.toLowerCase()
        : product.description.toLowerCase();
      
      let tags = currentLang !== 'zh' && product.translations?.tags
        ? product.translations.tags
        : product.tags;
        
      if (typeof tags === 'string') {
        tags = tags.split(',').map(tag => tag.trim());
      }
      if (!Array.isArray(tags)) {
        tags = [];
      }
      
      const searchTerm = value.toLowerCase();
      return title.includes(searchTerm) ||
        description.includes(searchTerm) ||
        tags.some(tag => tag.toLowerCase().includes(searchTerm));
    });
    
    const sortedResults = sortProducts(searchResult, sortOrder);
    setFilteredProducts(sortedResults);
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  // 添加获取分类名称的辅助函数
  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return categoryId;
    
    return currentLang === 'en' && category.translations?.en?.name
      ? category.translations.en.name
      : category.name;
  };

  // 添加排序处理函数
  const sortProducts = (productsToSort, order) => {
    const sorted = [...productsToSort];
    switch (order) {
      case 'priceAsc':
        return sorted.sort((a, b) => a.price - b.price);
      case 'priceDesc':
        return sorted.sort((a, b) => b.price - a.price);
      default:
        return sorted;
    }
  };

  // 处理排序变化
  const handleSortChange = (e) => {
    const newSortOrder = e.target.value;
    setSortOrder(newSortOrder);
    setFilteredProducts(sortProducts(filteredProducts, newSortOrder));
  };

  console.log('ProductList - Received products:', products);
  
  return (
    <div>
      <FilterSection>
        <FilterBar>
          <Radio.Group 
            value={activeCategory} 
            onChange={e => handleCategoryChange(e.target.value)}
            buttonStyle="outline"
          >
            {categories.map(cat => (
              <Radio.Button key={cat.id} value={cat.id}>
                {currentLang === 'en' && cat.translations?.en?.name
                  ? cat.translations.en.name
                  : cat.name}
              </Radio.Button>
            ))}
          </Radio.Group>
          <SortBar>
            <Radio.Group
              value={sortOrder}
              onChange={handleSortChange}
              buttonStyle="outline"
              size="small"
            >
              <Radio.Button value="default">
                {t('productList.sort.default')}
              </Radio.Button>
              <Radio.Button value="priceAsc">
                {t('productList.sort.priceAsc')}
              </Radio.Button>
              <Radio.Button value="priceDesc">
                {t('productList.sort.priceDesc')}
              </Radio.Button>
            </Radio.Group>
          </SortBar>
        </FilterBar>
        <SearchBar>
          <SearchInput
            value={searchValue}
            onChange={handleSearch}
            placeholder={t('productList.search.placeholder')}
          />
        </SearchBar>
      </FilterSection>

      {loading ? (
        <LoadingContainer className="initial-loading">
          <Spin size="large" />
        </LoadingContainer>
      ) : (
        <>
          <Row gutter={[12, 12]}>
            {filteredProducts.map((product, index) => {
              console.log('ProductList - Processing product:', {
                id: product.id,
                title: product.title,
                tags: product.tags,
                tagsType: typeof product.tags,
                isArray: Array.isArray(product.tags)
              });
              
              // 确保 tags 是数组
              const processedTags = Array.isArray(product.tags) 
                ? product.tags 
                : (typeof product.tags === 'string' 
                    ? product.tags.split(',').map(tag => tag.trim())
                    : []);

              const displayTitle = currentLang !== 'zh' && product.translations?.title
                ? product.translations.title
                : product.title;

              // 处理标签的翻译
              const displayTags = currentLang !== 'zh' && product.translations?.tags
                ? Array.isArray(product.translations.tags)
                  ? product.translations.tags
                  : typeof product.translations.tags === 'string'
                    ? product.translations.tags.split(',').map(tag => tag.trim())
                    : processedTags
                : processedTags;

              return (
                <Col 
                  key={product.id} 
                  xs={12} 
                  sm={12} 
                  md={8} 
                  lg={6}
                  ref={index === filteredProducts.length - 1 ? lastProductRef : null}
                >
                  <ProductCard
                    product={{
                      ...product,
                      title: displayTitle,
                      tags: displayTags
                    }}
                    categoryName={getCategoryName(product.category)}
                    onClick={() => handleProductClick(product.id)}
                    onShare={(product) => {
                      // 记录分享事件
                      console.log('Product shared:', {
                        productId: product.id,
                        title: product.title,
                        url: `${window.location.origin}/product/${product.id}`
                      });
                    }}
                    onLike={(product) => {
                      // 处理收藏逻辑
                      console.log('Like product:', product.id);
                    }}
                  />
                </Col>
              );
            })}
          </Row>
          {loadingMore && (
            <LoadingContainer>
              <Spin />
            </LoadingContainer>
          )}
          {!hasMore && filteredProducts.length > 0 && (
            <LoadingMore>
              {t('productList.loadingStatus.noMore')}
            </LoadingMore>
          )}
          {filteredProducts.length === 0 && (
            <LoadingMore>
              {t('productList.loadingStatus.empty')}
            </LoadingMore>
          )}
        </>
      )}
    </div>
  );
};

export default ProductList; 